// src/tabs/ContasAPagarSubTab.js
import React, { useState, useEffect, useMemo } from "react";
import { pb } from "../services/pocketbase";
import { formatCentavos, parseCentavos } from "../utils/moneyUtils";
import { financeiroService } from "../services/financeiroService";
import { 
  AlertCircle, 
  Calendar, 
  Check, 
  X, 
  Search, 
  Clock, 
  Filter, 
  Briefcase, 
  TrendingDown,
  Plus,
  Loader2
} from "lucide-react";

const getFirstDayOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};

const getLastDayOfMonth = () => {
  const d = new Date();
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
};

export default function ContasAPagarSubTab({ finance }) {
  const [contas, setContas] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selCategoria, setSelCategoria] = useState("");
  const [selCentro, setSelCentro] = useState("");

  // Filtros de Período e Situação (R3)
  const [filterDataInicio, setFilterDataInicio] = useState(getFirstDayOfMonth());
  const [filterDataFim, setFilterDataFim] = useState(getLastDayOfMonth());
  const [filterSituacao, setFilterSituacao] = useState("pendente"); // pendente, conciliada, todos

  // Liquidação Modal State
  const [selectedItem, setSelectedItem] = useState(null);
  const [isLiquidarModalOpen, setIsLiquidarModalOpen] = useState(false);
  const [liqConta, setLiqConta] = useState("");
  const [liqData, setLiqData] = useState("");
  const [liqFormaPagamento, setLiqFormaPagamento] = useState("Pix");

  // Cadastro de Conta Modal State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newVencimento, setNewVencimento] = useState("");
  const [newDescricao, setNewDescricao] = useState("");
  const [newValor, setNewValor] = useState("");
  const [newCategoria, setNewCategoria] = useState("");
  const [newCentroCusto, setNewCentroCusto] = useState("");
  const [newTipo, setNewTipo] = useState("a_vencer");
  const [newTipoCompromisso, setNewTipoCompromisso] = useState("");
  const [newObservacoes, setNewObservacoes] = useState("");

  // Recorrência Creation States
  const [isRecorrente, setIsRecorrente] = useState(false);
  const [recorrenciaAteMes, setRecorrenciaAteMes] = useState("12");
  const [recorrenciaAteAno, setRecorrenciaAteAno] = useState("2026");

  // Deletion Modal State
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleteScopeModalOpen, setIsDeleteScopeModalOpen] = useState(false);

  const hojeStr = useMemo(() => {
    return new Date().toISOString().split("T")[0];
  }, []);

  const openCreateModal = () => {
    setNewVencimento(hojeStr);
    setNewDescricao("");
    setNewValor("");
    setNewCategoria(finance.categorias.filter((c) => c.ativo && c.tipo === "despesa")[0]?.id || "");
    setNewCentroCusto(finance.centrosCusto.filter(c => c.ativo)[0]?.id || "");
    setNewTipo("a_vencer");
    setNewTipoCompromisso("");
    setNewObservacoes("");
    setIsRecorrente(false);
    setRecorrenciaAteMes("12");
    setRecorrenciaAteAno("2026");
    setIsCreateModalOpen(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!newDescricao.trim() || !newValor || !newCategoria || !newCentroCusto) {
      alert("Preencha todos os campos obrigatórios (*).");
      return;
    }

    try {
      finance.setLoading(true);
      const centsValue = -Math.abs(parseCentavos(newValor));

      if (isRecorrente) {
        // Geração de datas da recorrência mensal
        const start = new Date(newVencimento + "T12:00:00Z");
        const end = new Date(`${recorrenciaAteAno}-${String(recorrenciaAteMes).padStart(2, "0")}-28T12:00:00Z`);
        
        const originalDay = start.getDate();
        const occurrences = [];
        
        let current = new Date(start);
        while (current <= end) {
          occurrences.push(new Date(current));
          current.setMonth(current.getMonth() + 1);
          
          const expectedMonth = (occurrences[occurrences.length - 1].getMonth() + 1) % 12;
          if (current.getMonth() !== expectedMonth) {
            current.setDate(0);
          } else {
            const tempDate = new Date(current);
            tempDate.setDate(originalDay);
            if (tempDate.getMonth() === expectedMonth) {
              current.setDate(originalDay);
            }
          }
        }

        if (occurrences.length === 0) {
          throw new Error("A data limite de recorrência deve ser posterior à data de início.");
        }

        const uniqueHash = Math.random().toString(36).substring(2, 8).toUpperCase();
        const recurrenceId = "REC-" + uniqueHash;

        for (let i = 0; i < occurrences.length; i++) {
          const occDate = occurrences[i].toISOString().split("T")[0];
          const payload = {
            data: occDate,
            descricao: `${newDescricao.trim()} [Recorrência ${String(i + 1).padStart(2, "0")}/${String(occurrences.length).padStart(2, "0")}]`,
            valor_centavos: centsValue,
            categoria: newCategoria,
            centro_custo: newCentroCusto,
            status: newTipo,
            tipo_compromisso: newTipoCompromisso || null,
            conta_bancaria: null,
            forma_pagamento: null,
            observacoes: newObservacoes.trim() || null,
            origem_id: recurrenceId
          };
          await financeiroService.createLancamentoManual(payload, finance.user, finance.bloqueioRigido);
        }

      } else {
        // Lançamento normal único
        const payload = {
          data: newVencimento,
          descricao: newDescricao.trim(),
          valor_centavos: centsValue,
          categoria: newCategoria,
          centro_custo: newCentroCusto,
          status: newTipo,
          tipo_compromisso: newTipoCompromisso || null,
          conta_bancaria: null,
          forma_pagamento: null,
          observacoes: newObservacoes.trim() || null,
        };

        await financeiroService.createLancamentoManual(payload, finance.user, finance.bloqueioRigido);
      }

      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
      if (typeof finance.refreshTaxonomy === "function") {
        await finance.refreshTaxonomy();
      }

      setIsCreateModalOpen(false);
      alert("Conta(s) cadastrada(s) com sucesso!");
      await fetchContas();
    } catch (err) {
      alert("Erro ao cadastrar: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const fetchContas = async () => {
    try {
      finance.setLoading(true);
      
      let filterParts = ["valor_centavos < 0"];
      
      if (filterSituacao === "pendente") {
        filterParts.push('(status = "a_vencer" || status = "provisao")');
      } else if (filterSituacao === "conciliada") {
        filterParts.push('status = "pago"');
      }
      
      if (filterDataInicio) {
        filterParts.push(`data >= "${filterDataInicio}"`);
      }
      if (filterDataFim) {
        filterParts.push(`data <= "${filterDataFim}"`);
      }
      
      const filterStr = filterParts.join(" && ");
      
      const res = await pb.collection("fin_lancamentos").getFullList({
        filter: filterStr,
        sort: "data",
        expand: "categoria,centro_custo,conta_bancaria",
      });
      setContas(res || []);
    } catch (err) {
      console.warn("Erro ao buscar contas a pagar:", err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const handleDeletarClick = (item) => {
    if (item.origem_id && item.origem_id.startsWith("REC-")) {
      setItemToDelete(item);
      setIsDeleteScopeModalOpen(true);
    } else {
      if (window.confirm(`Tem certeza que deseja excluir a conta a pagar "${item.descricao}"?`)) {
        execDeletar(item.id, false);
      }
    }
  };

  const execDeletar = async (id, bulk = false, item = null) => {
    try {
      finance.setLoading(true);
      if (bulk && item) {
        // Busca e deleta todos do mesmo grupo com data >= item.data
        const matches = await pb.collection("fin_lancamentos").getFullList({
          filter: `origem_id = "${item.origem_id}" && status != "pago" && data >= "${item.data}"`
        });
        
        for (const m of matches) {
          await financeiroService.deleteLancamento(m.id, finance.user, finance.bloqueioRigido);
        }
        alert(`${matches.length} parcelas da recorrência foram excluídas com sucesso!`);
      } else {
        await financeiroService.deleteLancamento(id, finance.user, finance.bloqueioRigido);
        alert("Conta excluída com sucesso!");
      }
      
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
      if (typeof finance.refreshTaxonomy === "function") {
        await finance.refreshTaxonomy();
      }
      
      setIsDeleteScopeModalOpen(false);
      setItemToDelete(null);
      await fetchContas();
    } catch (err) {
      alert("Erro ao excluir: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  useEffect(() => {
    fetchContas();
  }, [filterDataInicio, filterDataFim, filterSituacao]);

  // Cálculos de Indicadores de Topo
  const stats = useMemo(() => {
    let totalVencido = 0;
    let totalVence7Dias = 0;
    let totalFuturo = 0;

    const seteDiasDepois = new Date();
    seteDiasDepois.setDate(seteDiasDepois.getDate() + 7);
    const seteDiasDepoisStr = seteDiasDepois.toISOString().split("T")[0];

    contas.forEach((c) => {
      if (c.status === "pago") return;
      const valor = Math.abs(c.valor_centavos || 0); // Contas a pagar são despesas (valores negativos), usamos absoluto para exibir nos cards
      if (c.data < hojeStr) {
        totalVencido += valor;
      } else if (c.data >= hojeStr && c.data <= seteDiasDepoisStr) {
        totalVence7Dias += valor;
      } else {
        totalFuturo += valor;
      }
    });

    return { totalVencido, totalVence7Dias, totalFuturo };
  }, [contas, hojeStr]);

  // Filtros aplicados
  const filteredContas = useMemo(() => {
    return contas.filter((c) => {
      const matchesSearch = searchQuery.trim() === "" || 
        c.descricao.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesCat = selCategoria === "" || c.categoria === selCategoria;
      const matchesCentro = selCentro === "" || c.centro_custo === selCentro;
      return matchesSearch && matchesCat && matchesCentro;
    });
  }, [contas, searchQuery, selCategoria, selCentro]);

  const openLiquidarModal = (item) => {
    setSelectedItem(item);
    setLiqConta(item.conta_bancaria || finance.contasBancarias.filter(c => c.ativo)[0]?.id || "");
    setLiqData(hojeStr);
    setLiqFormaPagamento("Pix");
    setIsLiquidarModalOpen(true);
  };

  const handleLiquidarSubmit = async (e) => {
    e.preventDefault();
    if (!selectedItem) return;

    try {
      finance.setLoading(true);
      
      // Atualiza o lançamento original de a_vencer/provisao para pago (Opção B)
      const dataUpdate = {
        status: "pago",
        conta_bancaria: liqConta || null,
        data: liqData,
        forma_pagamento: liqFormaPagamento.trim() || null,
      };

      const original = await pb.collection("fin_lancamentos").getOne(selectedItem.id);
      
      // Salva no banco de dados e executa os resumos/fechamento correspondentes via service
      const res = await pb.collection("fin_lancamentos").update(selectedItem.id, dataUpdate);

      // Sincroniza resumos e financiamentos
      // Chama o hook central para atualizar tudo
      if (typeof finance.refreshSaldos === "function") {
        await finance.refreshSaldos();
      }
      if (typeof finance.refreshTaxonomy === "function") {
        await finance.refreshTaxonomy();
      }

      // Decrementa saldo de financiamentos se houver match
      const financs = await pb.collection("fin_financiamentos").getFullList();
      const descUpper = original.descricao.toUpperCase();
      const finMatch = financs.find(f => descUpper.includes(f.nome.toUpperCase()));
      if (finMatch) {
        const novoSaldo = Math.max(0, (finMatch.saldo_devedor_centavos || 0) - Math.abs(original.valor_centavos));
        const novaParcela = Math.min(finMatch.parcelas_total, (finMatch.parcela_atual || 0) + 1);
        await pb.collection("fin_financiamentos").update(finMatch.id, {
          saldo_devedor_centavos: novoSaldo,
          parcela_atual: novaParcela
        });
      }

      setIsLiquidarModalOpen(false);
      alert("Conta liquidada com sucesso!");
      await fetchContas();
    } catch (err) {
      alert("Erro ao liquidar conta: " + err.message);
    } finally {
      finance.setLoading(false);
    }
  };

  const getStatusBadge = (item) => {
    if (item.data < hojeStr) {
      return (
        <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-wider bg-rose-50 text-rose-600 border border-rose-200 rounded animate-pulse">
          Atrasado
        </span>
      );
    }

    const seteDiasDepois = new Date();
    seteDiasDepois.setDate(seteDiasDepois.getDate() + 7);
    const seteDiasDepoisStr = seteDiasDepois.toISOString().split("T")[0];

    if (item.data <= seteDiasDepoisStr) {
      return (
        <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-wider bg-amber-50 text-amber-600 border border-amber-200 rounded">
          Vence em Breve
        </span>
      );
    }

    return (
      <span className="px-2 py-0.5 text-[9px] font-black uppercase tracking-wider bg-blue-50 text-blue-600 border border-blue-200 rounded">
        Agendado
      </span>
    );
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      
      {/* ─── CARDS DE INDICADORES DE TOPOS ──────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4 lg:p-6 pb-0 shrink-0">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-rose-50 text-rose-600 rounded-lg flex items-center justify-center">
              <AlertCircle size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Total Atrasado</span>
              <h4 className="text-lg font-black text-rose-600 font-mono">
                {formatCentavos(stats.totalVencido)}
              </h4>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-amber-50 text-amber-600 rounded-lg flex items-center justify-center">
              <Clock size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Vence em 7 dias</span>
              <h4 className="text-lg font-black text-amber-600 font-mono">
                {formatCentavos(stats.totalVence7Dias)}
              </h4>
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between shadow-sm">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-slate-50 text-slate-600 rounded-lg flex items-center justify-center">
              <Calendar size={20} />
            </div>
            <div>
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Previsto Futuro</span>
              <h4 className="text-lg font-black text-slate-700 font-mono">
                {formatCentavos(stats.totalFuturo)}
              </h4>
            </div>
          </div>
        </div>
      </div>

      {/* ─── FILTROS DE PESQUISA E SELETORES ────────────────────────────── */}
      <div className="bg-white border-b border-slate-200 p-4 shrink-0 flex flex-col gap-3">
        <div className="flex flex-col lg:flex-row gap-3 items-stretch lg:items-center">
          
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <input
              type="text"
              placeholder="Pesquisar por credor, descrição..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-lg outline-none text-xs"
            />
          </div>

          <div className="flex flex-wrap gap-2 items-center">
            {/* Filtros de data e situação (R3) */}
            <div className="flex items-center gap-1.5 border border-slate-200 rounded-lg bg-slate-50 p-1">
              <span className="text-[10px] font-black uppercase text-slate-400 px-1.5">Período:</span>
              <input
                type="date"
                value={filterDataInicio}
                onChange={(e) => setFilterDataInicio(e.target.value)}
                className="bg-transparent text-xs font-semibold text-slate-650 outline-none w-28"
                title="Data Início"
              />
              <span className="text-slate-300 text-xs">/</span>
              <input
                type="date"
                value={filterDataFim}
                onChange={(e) => setFilterDataFim(e.target.value)}
                className="bg-transparent text-xs font-semibold text-slate-650 outline-none w-28"
                title="Data Fim"
              />
              {(filterDataInicio || filterDataFim) && (
                <button
                  type="button"
                  onClick={() => {
                    setFilterDataInicio("");
                    setFilterDataFim("");
                  }}
                  className="text-slate-400 hover:text-slate-650 p-0.5"
                  title="Limpar período"
                >
                  <X size={12} />
                </button>
              )}
            </div>

            <select
              value={filterSituacao}
              onChange={(e) => setFilterSituacao(e.target.value)}
              className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
            >
              <option value="pendente">Apenas Pendentes</option>
              <option value="conciliada">Apenas Pagas</option>
              <option value="todos">Todas Situações</option>
            </select>

            <select
              value={selCategoria}
              onChange={(e) => setSelCategoria(e.target.value)}
              className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
            >
              <option value="">Todas Categorias</option>
              {finance.categorias.map((c) => (
                <option key={c.id} value={c.id}>{c.nome}</option>
              ))}
            </select>

            <select
              value={selCentro}
              onChange={(e) => setSelCentro(e.target.value)}
              className="text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-lg p-2 outline-none"
            >
              <option value="">Todos Centros</option>
              {finance.centrosCusto.map((cc) => (
                <option key={cc.id} value={cc.id}>{cc.nome}</option>
              ))}
            </select>

            <button
              onClick={openCreateModal}
              className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-3.5 py-2 rounded-lg text-xs font-bold shadow-sm transition-colors uppercase tracking-wider ml-auto animate-fade-in"
            >
              <Plus size={14} /> Lançar Conta a Pagar
            </button>
          </div>

        </div>
      </div>

      {/* ─── LISTAGEM DE CONTAS A PAGAR ────────────────────────────────── */}
      <div className="flex-1 overflow-auto bg-white p-4">
        {filteredContas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 gap-3">
            <Check size={40} className="text-emerald-500 bg-emerald-50 p-2 rounded-full border border-emerald-100" />
            <span className="text-xs font-black text-slate-500 uppercase tracking-wider">Nenhuma conta pendente encontrada</span>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-xl overflow-hidden shadow-xs">
            <table className="w-full text-left border-collapse bg-white">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400">Vencimento</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400">Status</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400">Descrição</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400">Centro de Custo</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400">Categoria</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400 text-right">Valor</th>
                  <th className="px-4 py-3 text-[10px] font-black uppercase text-slate-400 text-center w-36">Ação</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredContas.map((item) => (
                  <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 text-xs font-black text-slate-700 font-mono">
                      {(item.data || "").substring(0, 10).split("-").reverse().join("/")}
                    </td>
                    <td className="px-4 py-3 text-xs font-semibold">
                      {getStatusBadge(item)}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold text-slate-800">
                      {item.descricao}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-600">
                      {item.expand?.centro_custo?.nome || "Geral"}
                    </td>
                    <td className="px-4 py-3 text-xs font-medium text-slate-600">
                      {item.expand?.categoria?.nome || "Sem Categoria"}
                    </td>
                    <td className="px-4 py-3 text-xs font-black text-right text-rose-600 font-mono">
                      {formatCentavos(item.valor_centavos)}
                    </td>
                    <td className="px-4 py-3 text-center flex items-center justify-center gap-1.5">
                      <button
                        onClick={() => openLiquidarModal(item)}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] px-3.5 py-1.5 rounded-lg shadow-sm transition-colors uppercase tracking-wider"
                      >
                        Pagar
                      </button>
                      <button
                        onClick={() => handleDeletarClick(item)}
                        className="bg-rose-50 hover:bg-rose-100 text-rose-600 border border-rose-200 font-bold text-[10px] px-3 py-1.5 rounded-lg shadow-sm transition-colors uppercase tracking-wider"
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ─── MODAL: LIQUIDAR CONTA ─────────────────────────────────────── */}
      {isLiquidarModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide font-black">Dar Baixa (Confirmar Pagamento)</h4>
              <button onClick={() => setIsLiquidarModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleLiquidarSubmit} className="p-5 space-y-4">
              <div>
                <span className="text-[10px] font-bold text-slate-400 block mb-1">Título/Descrição:</span>
                <span className="text-xs font-black text-slate-700 block">{selectedItem?.descricao}</span>
                <span className="text-xs font-black text-rose-600 block mb-4 font-mono">
                  Valor a Pagar: {formatCentavos(selectedItem?.valor_centavos)}
                </span>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data do Pagamento *</label>
                <input
                  type="date"
                  required
                  value={liqData}
                  onChange={(e) => setLiqData(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Conta Bancária de Origem *</label>
                <select
                  required
                  value={liqConta}
                  onChange={(e) => setLiqConta(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                >
                  <option value="" disabled>Selecione a conta...</option>
                  {finance.contasBancarias.filter(c => c.ativo).map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Forma de Pagamento</label>
                <input
                  type="text"
                  value={liqFormaPagamento}
                  onChange={(e) => setLiqFormaPagamento(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Pix, Cartão, Dinheiro..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsLiquidarModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Confirmar Baixa
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: CADASTRAR CONTA A PAGAR ─────────────────────────────── */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col relative">
            {finance.loading && (
              <div className="absolute inset-0 bg-white/70 backdrop-blur-[0.5px] flex flex-col items-center justify-center z-50 animate-fade-in">
                <Loader2 className="animate-spin text-slate-900 mb-2" size={32} />
                <span className="text-xs font-black text-slate-700 uppercase tracking-wider animate-pulse">
                  {isRecorrente ? "Criando recorrência..." : "Salvando despesa..."}
                </span>
              </div>
            )}
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide font-black">Lançar Nova Conta a Pagar</h4>
              <button onClick={() => setIsCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleCreateSubmit} className="p-5 space-y-4 overflow-y-auto max-h-[75vh]">
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Descrição / Credor *</label>
                <input
                  type="text"
                  required
                  value={newDescricao}
                  onChange={(e) => setNewDescricao(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  placeholder="Ex: Fornecedor de Embalagens, Energia Elétrica"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Valor (R$) *</label>
                  <input
                    type="text"
                    required
                    value={newValor}
                    onChange={(e) => setNewValor(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-bold text-right"
                    placeholder="0,00"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Vencimento *</label>
                  <input
                    type="date"
                    required
                    value={newVencimento}
                    onChange={(e) => setNewVencimento(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                  <select
                    required
                    value={newCategoria}
                    onChange={(e) => setNewCategoria(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    {finance.categorias.filter((c) => c.ativo && c.tipo === "despesa").map((c) => (
                      <option key={c.id} value={c.id}>{c.nome}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Centro de Custo *</label>
                  <select
                    required
                    value={newCentroCusto}
                    onChange={(e) => setNewCentroCusto(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    {finance.centrosCusto.filter(c => c.ativo).map((cc) => (
                      <option key={cc.id} value={cc.id}>{cc.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Opção Despesa Recorrente */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 space-y-3">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="isRecorrente"
                    checked={isRecorrente}
                    onChange={(e) => setIsRecorrente(e.target.checked)}
                    className="w-4 h-4 text-blue-600 focus:ring-blue-550 border-slate-350 rounded cursor-pointer"
                  />
                  <label htmlFor="isRecorrente" className="text-xs font-black text-slate-750 uppercase select-none cursor-pointer">
                    Despesa Fixa Recorrente?
                  </label>
                </div>

                {isRecorrente && (
                  <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60 animate-fade-in">
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Repetir Mensal Até:</label>
                      <select
                        value={recorrenciaAteMes}
                        onChange={(e) => setRecorrenciaAteMes(e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[9px] font-bold text-slate-500 uppercase mb-1">Ano Limite:</label>
                      <select
                        value={recorrenciaAteAno}
                        onChange={(e) => setRecorrenciaAteAno(e.target.value)}
                        className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs font-semibold bg-white outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
                      >
                        {[2025, 2026, 2027, 2028].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Situação do Lançamento *</label>
                  <select
                    value={newTipo}
                    onChange={(e) => setNewTipo(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="a_vencer">A Vencer (Boleto/Fatura real)</option>
                    <option value="provisao">Provisão (Estimativa/Previsão de gasto)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Tipo de Compromisso</label>
                  <select
                    value={newTipoCompromisso}
                    onChange={(e) => setNewTipoCompromisso(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 font-semibold"
                  >
                    <option value="">Nenhum...</option>
                    {finance.tiposCompromisso && finance.tiposCompromisso.filter(t => t.ativo !== false).map((t) => (
                      <option key={t.id} value={t.id}>{t.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Observações</label>
                <textarea
                  value={newObservacoes}
                  onChange={(e) => setNewObservacoes(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-350 rounded-lg text-xs bg-white outline-none focus:ring-2 focus:ring-blue-500 h-20 resize-none"
                  placeholder="Alguma nota adicional..."
                />
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-500 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex items-center gap-1 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-lg text-xs font-bold shadow transition-colors"
                >
                  <Check size={16} /> Confirmar Lançamento
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── MODAL: CONFIRMAR ESCOPO DE EXCLUSÃO ────────────────────────── */}
      {isDeleteScopeModalOpen && itemToDelete && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-xl shadow-2xl border border-slate-100 w-full max-w-md overflow-hidden flex flex-col">
            <div className="bg-slate-900 text-white p-4 flex justify-between items-center shrink-0">
              <h4 className="font-bold text-sm uppercase tracking-wide font-black">Excluir Conta Recorrente</h4>
              <button onClick={() => setIsDeleteScopeModalOpen(false)} className="text-slate-400 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              <p className="text-xs text-slate-600 font-semibold">
                O lançamento <strong className="text-slate-800">"{itemToDelete.descricao}"</strong> faz parte de uma recorrência ativa. Como deseja prosseguir com a exclusão?
              </p>

              <div className="flex flex-col gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => execDeletar(itemToDelete.id, false)}
                  className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs py-2.5 rounded-lg transition-colors uppercase tracking-wider text-center"
                >
                  Excluir apenas esta parcela
                </button>
                <button
                  type="button"
                  onClick={() => execDeletar(itemToDelete.id, true, itemToDelete)}
                  className="w-full bg-rose-650 hover:bg-rose-700 text-white font-bold text-xs py-2.5 rounded-lg transition-colors uppercase tracking-wider text-center shadow-sm"
                >
                  Cancelar Recorrência (Excluir esta e parcelas futuras)
                </button>
                <button
                  type="button"
                  onClick={() => setIsDeleteScopeModalOpen(false)}
                  className="w-full bg-transparent hover:bg-slate-50 text-slate-400 hover:text-slate-500 font-bold text-xs py-2 rounded-lg transition-colors uppercase tracking-wider text-center cursor-pointer"
                >
                  Voltar / Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
